<script setup lang="ts">
const route = useRoute()
const { formatDate } = useFormatDate()

const { data: post } = await useAsyncData(route.path, () => queryCollection('posts').path(route.path).first())
if (!post.value) {
  throw createError({ statusCode: 404, statusMessage: 'Post not found', fatal: true })
}

const { data: surround } = await useAsyncData(`${route.path}-surround`, () => {
  return queryCollectionItemSurroundings('posts', route.path, {
    fields: ['description']
  })
})

const title = post.value.seo?.title || post.value.title
const description = (post.value.seo?.description || post.value.description || '')
  .replace(/\*\*(.+?)\*\*/g, '$1')
  .replace(/\*(.+?)\*/g, '$1')
  .replace(/\s*\n\s*/g, ' ')
  .trim()

const renderedDescription = (post.value.description || '').replace(/\n+/g, '\n\n')

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
  ogType: 'article',
  articlePublishedTime: post.value.date ? new Date(post.value.date).toISOString() : undefined,
  ogImageAlt: post.value.image?.alt || title
})

if (post.value.image?.src) {
  defineOgImage({
    url: post.value.image.src
  })
} else {
  defineOgImageComponent('Saas', {
    headline: 'Blog'
  })
}

function authorId(to: string | null | undefined): string | undefined {
  if (!to) return undefined
  return to === '/ueber-uns/dozenten/rolf-eggenberger' ? PERSON_ROLF_ID : `${absoluteUrl(to)}#person`
}

useJsonLd(() => post.value
  ? {
      '@type': 'BlogPosting',
      '@id': `${absoluteUrl(route.path)}/#article`,
      'headline': post.value.title,
      description,
      'url': absoluteUrl(route.path),
      'image': post.value.image?.src ? absoluteUrl(post.value.image.src) : undefined,
      'datePublished': post.value.date,
      'inLanguage': 'de-CH',
      'publisher': { '@id': ORGANIZATION_ID },
      // Nicht jede Autorin hat eine eigene Profilseite (z. B. Gastbeiträge) –
      // `to` kann leer sein, dann ohne `@id` referenzieren statt abzustürzen.
      'author': (post.value.authors ?? []).map(author => ({
        '@type': 'Person',
        ...(authorId(author.to) ? { '@id': authorId(author.to) } : {}),
        'name': author.name
      })),
      'about': post.value.badge?.label
    }
  : null, 'blogposting-schema')
</script>

<template>
  <UContainer v-if="post">
    <UPageHeader :title="post.title">
      <template #description>
        <p
          v-if="post.subtitle"
          class="text-xl text-default font-medium mb-3"
        >
          {{ post.subtitle }}
        </p>
        <MDC
          :value="renderedDescription"
          class="prose-description"
        />
      </template>

      <template #headline>
        <div class="flex flex-wrap items-center gap-2">
          <NuxtLink
            v-for="label in post.badge?.label ?? []"
            :key="label"
            :to="`/blog/thema/${labelToSlug(label)}`"
          >
            <UBadge
              :label="label"
              variant="subtle"
            />
          </NuxtLink>
        </div>
        <span class="text-muted">&middot;</span>
        <time class="text-muted">{{ formatDate(post.date) }}</time>
      </template>

      <div class="flex flex-wrap items-center gap-3 mt-4">
        <UButton
          v-for="(author, index) in post.authors"
          :key="index"
          :to="author.to"
          color="neutral"
          variant="subtle"
          target="_blank"
          size="sm"
        >
          <UAvatar
            v-bind="author.avatar"
            alt="Author avatar"
            size="2xs"
          />

          {{ author.name }}
        </UButton>
      </div>
    </UPageHeader>

    <UPage>
      <UPageBody>
        <figure
          v-if="post.image?.src"
          class="mb-8"
        >
          <NuxtImg
            :src="post.image.src"
            :alt="post.image.alt || post.title"
            :title="post.image.alt || post.title"
            class="w-full rounded-xl object-cover max-h-[420px]"
            loading="eager"
          />
          <figcaption
            v-if="post.image.label"
            class="text-sm text-muted text-left mt-2 italic"
          >
            {{ post.image.label }}
          </figcaption>
        </figure>

        <ContentRenderer
          v-if="post"
          :value="post"
        />

        <template v-if="post.cta">
          <USeparator class="my-10" />
          <UPageCTA
            v-bind="post.cta"
            variant="subtle"
            class="rounded-xl"
          />
        </template>
      </UPageBody>

      <!-- Rechte Spalte immer bereitstellen, damit die Textbreite konstant bleibt –
           das Inhaltsverzeichnis erscheint nur, wenn es In-Page-Links gibt. -->
      <template #right>
        <UContentToc
          v-if="post?.body?.toc?.links?.length"
          title="Auf dieser Seite:"
          :links="post.body.toc.links"
        />
      </template>
    </UPage>
  </UContainer>
</template>
